import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownMenu,
  IconButton,
  Primitive,
  size,
} from '@rtkwlf/fenrir-react';
import { faGrid } from '@rtkwlf/pro-solid-svg-icons/faGrid';
import { useTranslation } from 'react-i18next';

import { useLegacyCapabilities } from '@apis/rendall/queries/use-capabilities';
import { useGetUserInfo } from '@packages/legacy/auth';
import { useRendallOrganization } from 'ae-kernel/organizations/use-rendall-organization';

export const AppSwitcher: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const { currentOrganization, organizationId } = useRendallOrganization();
  const { data: legacyCapabilities, isLoading } = useLegacyCapabilities();
  const user = useGetUserInfo();

  const usernameQueryStringParam = React.useMemo(() => {
    if (user?.email) {
      return `?username=${encodeURIComponent(user.email)}`;
    }
    return '';
  }, [user]);

  /**
   * Create a config map of the items that should render in the app switcher.
   * This is important because we only want to show apps that the customer has subscribed to.
   */
  const switcherItemConfig = React.useMemo(() => {
    const config: { label: string; href: string }[] = [];

    // if the user has managed risk
    if (legacyCapabilities?.canAccessManageRisk) {
      config.push({
        href: `https://risk.arcticwolf.com/?id=${organizationId}`,
        label: t('appBar:switchToMenu.riskPortal'),
      });
    }

    // if the user has incident response jumpstart
    if (legacyCapabilities?.canAccessIncidentResponse) {
      config.push({
        href: `https://cyberjumpstart.arcticwolf.com/awn-organization/${currentOrganization?.customerID}${usernameQueryStringParam}`,
        label: t('appBar:switchToMenu.myCyber'),
      });
    }

    // if the user has managed security awareness
    if (legacyCapabilities?.canAccessManageAwareness) {
      config.push({
        href: `https://sat.arcticwolf.com/Home/Dashboard?cid=${currentOrganization?.customerID}`,
        label: t('appBar:switchToMenu.managedSecurityAwareness'),
      });
    }

    // All users will have access to cra
    config.push({
      href: `https://assessment.arcticwolf.com/awn-organization/${currentOrganization?.customerID}${usernameQueryStringParam}`,
      label: t('appBar:switchToMenu.cyberResilienceAssessment'),
    });

    return config;
  }, [
    legacyCapabilities?.canAccessManageRisk,
    legacyCapabilities?.canAccessIncidentResponse,
    legacyCapabilities?.canAccessManageAwareness,
    organizationId,
    t,
    currentOrganization?.customerID,
    usernameQueryStringParam,
  ]);

  const hasNoProducts = switcherItemConfig.length === 0;
  if (!isLoading && hasNoProducts) {
    return <Primitive.div height="2rem" width="2rem" />;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <IconButton aria-label="switch applications" isLoading={isLoading}>
          <FontAwesomeIcon icon={faGrid} />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" style={{ minWidth: size['25rem'] }}>
        <DropdownMenu.Label>
          {t('appBar:switchToMenu.header')}
        </DropdownMenu.Label>
        <DropdownMenu.Group>
          {switcherItemConfig.map((i) => (
            <DropdownMenu.ExternalLinkItem
              key={i.label}
              href={i.href}
              target="_blank"
            >
              <DropdownMenu.ExternalLinkItemText>
                {i.label}
              </DropdownMenu.ExternalLinkItemText>
            </DropdownMenu.ExternalLinkItem>
          ))}
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
